import { useEffect, useRef, useState } from "react";
import { Plus, Trash2, X } from "lucide-react";
import { loadGooglePlacesLibrary } from "../lib/googleMaps";
import type {
  FacilitySearchResult,
  Facility,
  FacilityType,
  FacilityVeterinarian,
  Pet,
} from "../lib/api";

export type FacilityFormData = {
  name: string;
  type: FacilityType;
  contact_name: string;
  email: string;
  phone: string;
  website: string;
  address_line1: string;
  address_line2: string;
  city: string;
  region: string;
  postal_code: string;
  owner_email: string;
  pet_ids: string[];
  veterinarians: Omit<FacilityVeterinarian, "id">[];
};

type FacilitySearchTarget = "name" | "address" | null;

const emptyForm: FacilityFormData = {
  name: "",
  type: "VETERINARY_CLINIC",
  contact_name: "",
  email: "",
  phone: "",
  website: "",
  address_line1: "",
  address_line2: "",
  city: "",
  region: "",
  postal_code: "",
  owner_email: "",
  pet_ids: [],
  veterinarians: [],
};

function ExistingFacilitySuggestions({
  matches,
  onSelect,
  id,
}: {
  matches: FacilitySearchResult[];
  onSelect: (facility: FacilitySearchResult) => void;
  id: string;
}) {
  return (
    <div
      id={id}
      className="google-address-suggestions"
      role="listbox"
      aria-label="Facilities in Actapet"
    >
      <div className="google-address-group-label">In Actapet</div>
      {matches.map((facility) => (
        <button
          type="button"
          role="option"
          className="google-address-suggestion"
          key={facility.id}
          onClick={() => onSelect(facility)}
        >
          <strong>{facility.name}</strong>
          <span>
            {[
              facility.address_line1,
              facility.city,
              facility.region,
              facility.postal_code,
            ]
              .filter(Boolean)
              .join(", ")}
          </span>
        </button>
      ))}
    </div>
  );
}

export default function FacilityForm({
  mode,
  pets = [],
  onClose,
  onSave,
  initialFacility,
  onSearchFacilities,
  onSaveExisting,
}: {
  mode: "admin" | "pet-parent";
  pets?: Pet[];
  onClose: () => void;
  onSave: (facility: FacilityFormData) => Promise<void>;
  initialFacility?: Facility;
  onSearchFacilities?: (query: string) => Promise<FacilitySearchResult[]>;
  onSaveExisting?: (facilityId: string, petIds: string[]) => Promise<void>;
}) {
  const [form, setForm] = useState<FacilityFormData>(() => initialFacility ? {
    ...emptyForm,
    name: initialFacility.name,
    type: initialFacility.type,
    contact_name: initialFacility.contact_name || '',
    email: initialFacility.email || '',
    phone: initialFacility.phone || '',
    website: initialFacility.website || '',
    address_line1: initialFacility.address_line1,
    address_line2: initialFacility.address_line2 || '',
    city: initialFacility.city,
    region: initialFacility.region,
    postal_code: initialFacility.postal_code,
    veterinarians: initialFacility.veterinarians.map(({ name, email, phone, license_number }) => ({ name, email, phone, license_number })),
  } : emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [addressSelected, setAddressSelected] = useState(Boolean(initialFacility));
  const [addressError, setAddressError] = useState("");
  const [addressQuery, setAddressQuery] = useState(initialFacility?.address_line1 ?? "");
  const [facilitySearchQuery, setFacilitySearchQuery] = useState("");
  const [facilitySearchTarget, setFacilitySearchTarget] =
    useState<FacilitySearchTarget>(null);
  const [addressSuggestions, setAddressSuggestions] = useState<
    google.maps.places.AutocompleteSuggestion[]
  >([]);
  const [existingFacilityMatches, setExistingFacilityMatches] = useState<
    FacilitySearchResult[]
  >([]);
  const [existingFacility, setExistingFacility] =
    useState<FacilitySearchResult | null>(null);
  const [placesLibrary, setPlacesLibrary] =
    useState<google.maps.PlacesLibrary | null>(null);
  const [searchingAddresses, setSearchingAddresses] = useState(false);
  const [searchingFacilities, setSearchingFacilities] = useState(false);
  const sessionToken = useRef<google.maps.places.AutocompleteSessionToken>();
  const searchSequence = useRef(0);
  const facilitySearchSequence = useRef(0);
  const isAdmin = mode === "admin";

  useEffect(() => {
    let active = true;
    void loadGooglePlacesLibrary()
      .then((library) => {
        if (active) setPlacesLibrary(library);
      })
      .catch((loadError: unknown) => {
        if (active)
          setAddressError(
            loadError instanceof Error
              ? loadError.message
              : "Unable to load Google Maps address search.",
          );
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    const input = addressQuery.trim();
    if (!placesLibrary || input.length < 3 || addressSelected) {
      searchSequence.current += 1;
      setAddressSuggestions([]);
      setSearchingAddresses(false);
      return;
    }

    const sequence = ++searchSequence.current;
    const timeout = window.setTimeout(() => {
      setSearchingAddresses(true);
      sessionToken.current ??= new placesLibrary.AutocompleteSessionToken();
      void placesLibrary.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken: sessionToken.current,
      })
        .then(({ suggestions }) => {
          if (searchSequence.current === sequence)
            setAddressSuggestions(
              suggestions.filter((suggestion) => suggestion.placePrediction),
            );
        })
        .catch(() => {
          if (searchSequence.current === sequence)
            setAddressError(
              "Unable to search Google Maps addresses. Please try again.",
            );
        })
        .finally(() => {
          if (searchSequence.current === sequence) setSearchingAddresses(false);
        });
    }, 250);

    return () => window.clearTimeout(timeout);
  }, [addressQuery, addressSelected, placesLibrary]);

  useEffect(() => {
    const query = facilitySearchQuery.trim();
    if (
      isAdmin ||
      !onSearchFacilities ||
      query.length < 2 ||
      existingFacility ||
      !facilitySearchTarget
    ) {
      facilitySearchSequence.current += 1;
      setExistingFacilityMatches([]);
      setSearchingFacilities(false);
      return;
    }

    const sequence = ++facilitySearchSequence.current;
    const timeout = window.setTimeout(() => {
      setSearchingFacilities(true);
      void onSearchFacilities(query)
        .then((matches) => {
          if (facilitySearchSequence.current === sequence)
            setExistingFacilityMatches(matches);
        })
        .catch(() => {
          if (facilitySearchSequence.current === sequence)
            setAddressError(
              "Unable to search Actapet facilities. Please try again.",
            );
        })
        .finally(() => {
          if (facilitySearchSequence.current === sequence)
            setSearchingFacilities(false);
        });
    }, 200);

    return () => window.clearTimeout(timeout);
  }, [
    facilitySearchQuery,
    facilitySearchTarget,
    existingFacility,
    isAdmin,
    onSearchFacilities,
  ]);

  const selectAddress = async (
    prediction: google.maps.places.PlacePrediction,
  ) => {
    try {
      searchSequence.current += 1;
      const place = prediction.toPlace();
      await place.fetchFields({ fields: ["addressComponents"] });
      const components = place.addressComponents ?? [];
      const getPart = (type: string) =>
        components.find((component) => component.types.includes(type))
          ?.longText ?? "";
      const streetNumber = getPart("street_number");
      const route = getPart("route");
      const street = [streetNumber, route].filter(Boolean).join(" ");
      const city =
        getPart("locality") ||
        getPart("postal_town") ||
        getPart("sublocality_level_1");
      const region = getPart("administrative_area_level_1");
      const postalCode = [getPart("postal_code"), getPart("postal_code_suffix")]
        .filter(Boolean)
        .join("-");
      if (!street || !city || !region || !postalCode) {
        setAddressSelected(false);
        setAddressError("Choose a result with a complete street address.");
        return;
      }
      setForm((current) => ({
        ...current,
        address_line1: street,
        city,
        region,
        postal_code: postalCode,
      }));
      setAddressQuery(prediction.text.toString());
      setAddressSuggestions([]);
      setAddressSelected(true);
      setAddressError("");
      setFacilitySearchQuery("");
      setFacilitySearchTarget(null);
      sessionToken.current = undefined;
    } catch {
      setAddressError(
        "Unable to verify this address. Please choose another result.",
      );
    }
  };

  const selectExistingFacility = (facility: FacilitySearchResult) => {
    searchSequence.current += 1;
    facilitySearchSequence.current += 1;
    setExistingFacility(facility);
    setExistingFacilityMatches([]);
    setAddressSuggestions([]);
    setAddressQuery(facility.address_line1);
    setFacilitySearchQuery("");
    setFacilitySearchTarget(null);
    setAddressSelected(true);
    setAddressError("");
    setForm((current) => ({
      ...current,
      name: facility.name,
      type: facility.type,
      contact_name: facility.contact_name || "",
      email: facility.email || "",
      phone: facility.phone || "",
      website: facility.website || "",
      address_line1: facility.address_line1,
      address_line2: facility.address_line2 || "",
      city: facility.city,
      region: facility.region,
      postal_code: facility.postal_code,
      veterinarians: facility.veterinarians.map(
        ({ name, email, phone, license_number }) => ({
          name,
          email,
          phone,
          license_number,
        }),
      ),
    }));
  };

  const clearExistingFacility = () => {
    setExistingFacility(null);
    setAddressQuery("");
    setFacilitySearchQuery("");
    setFacilitySearchTarget(null);
    setAddressSelected(false);
    setAddressError("");
    setForm((current) => ({ ...emptyForm, pet_ids: current.pet_ids }));
  };

  const update = (
    field: keyof Omit<FacilityFormData, "veterinarians" | "pet_ids">,
    value: string,
  ) => setForm((current) => ({ ...current, [field]: value }));
  const updateVet = (
    index: number,
    field: keyof FacilityVeterinarian,
    value: string,
  ) =>
    setForm((current) => ({
      ...current,
      veterinarians: current.veterinarians.map((vet, vetIndex) =>
        vetIndex === index ? { ...vet, [field]: value } : vet,
      ),
    }));
  const togglePet = (petId: string) =>
    setForm((current) => ({
      ...current,
      pet_ids: current.pet_ids.includes(petId)
        ? current.pet_ids.filter((id) => id !== petId)
        : [...current.pet_ids, petId],
    }));

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!isAdmin && !form.pet_ids.length) {
      setError("Select at least one pet that has used this facility.");
      return;
    }
    if (!existingFacility && !addressSelected) {
      setError("Choose a verified Google Maps address before continuing.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      if (existingFacility) {
        if (!onSaveExisting) throw new Error("Unable to add this existing facility.");
        await onSaveExisting(existingFacility.id, form.pet_ids);
        return;
      }
      await onSave({
        ...form,
        veterinarians: form.veterinarians
          .filter((vet) => vet.name.trim())
          .map((vet) => ({ ...vet, name: vet.name.trim() })),
      });
    } catch (saveError) {
      setError(
        saveError instanceof Error
          ? saveError.message
          : "Unable to create facility",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <form className="modal facility-form-modal" onSubmit={submit}>
        <div className="modal-heading">
          <div>
            <p className="eyebrow">{isAdmin ? "Administration" : "Pet care"}</p>
            <h2>{initialFacility ? "Edit facility" : "Add facility"}</h2>
          </div>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close form"
          >
            <X size={19} />
          </button>
        </div>
        <fieldset
          className="facility-fields"
          disabled={Boolean(existingFacility)}
        >
        <div className="form-grid">
          <label>
            Facility name
            <div className="google-address-search">
              <input
                required
                value={form.name}
                aria-label="Facility name"
                aria-expanded={
                  facilitySearchTarget === "name" &&
                  existingFacilityMatches.length > 0
                }
                aria-controls="facility-name-suggestions"
                onChange={(event) => {
                  update("name", event.target.value);
                  setFacilitySearchQuery(event.target.value);
                  setFacilitySearchTarget("name");
                  setExistingFacilityMatches([]);
                  setAddressError("");
                }}
                placeholder="Search facility name"
              />
              {facilitySearchTarget === "name" && searchingFacilities && (
                <span className="google-address-status" role="status">
                  Searching...
                </span>
              )}
              {facilitySearchTarget === "name" &&
                existingFacilityMatches.length > 0 && (
                  <ExistingFacilitySuggestions
                    id="facility-name-suggestions"
                    matches={existingFacilityMatches}
                    onSelect={selectExistingFacility}
                  />
                )}
            </div>
          </label>
          <label>
            Facility type
            <select
              value={form.type}
              onChange={(event) =>
                update("type", event.target.value as FacilityType)
              }
            >
              <option value="VETERINARY_CLINIC">Veterinary Clinic</option>
              <option value="BOARDING_FACILITY">Boarding Facility</option>
              <option value="TRAINING_FACILITY">Training Facility</option>
              <option value="OTHER">Other</option>
            </select>
          </label>
          <label>
            Contact name
            <input
              value={form.contact_name}
              onChange={(event) => update("contact_name", event.target.value)}
            />
          </label>
          <label>
            Facility email
            <input
              type="email"
              value={form.email}
              onChange={(event) => update("email", event.target.value)}
            />
          </label>
          <label>
            Phone
            <input
              type="tel"
              value={form.phone}
              onChange={(event) => update("phone", event.target.value)}
            />
          </label>
          <label>
            Website
            <input
              type="url"
              value={form.website}
              onChange={(event) => update("website", event.target.value)}
              placeholder="https://"
            />
          </label>
          <label className="full-field">
            Street address
            <div className="google-address-search">
              <input
                type="text"
                autoComplete="street-address"
                aria-label="Street address"
                aria-expanded={
                  (facilitySearchTarget === "address" &&
                    existingFacilityMatches.length > 0) ||
                  addressSuggestions.length > 0
                }
                aria-controls="facility-address-suggestions"
                placeholder="Search Google Maps addresses"
                value={addressQuery}
                onChange={(event) => {
                  searchSequence.current += 1;
                  setAddressQuery(event.target.value);
                  setFacilitySearchQuery(event.target.value);
                  setFacilitySearchTarget("address");
                  setAddressSelected(false);
                  setAddressError("");
                  setAddressSuggestions([]);
                  setExistingFacilityMatches([]);
                  setForm((current) => ({
                    ...current,
                    address_line1: "",
                    city: "",
                    region: "",
                    postal_code: "",
                  }));
                }}
                onKeyDown={(event) => {
                  if (event.key === "Escape") setAddressSuggestions([]);
                }}
              />
              {(searchingAddresses ||
                (facilitySearchTarget === "address" && searchingFacilities)) && (
                <span className="google-address-status" role="status">
                  Searching...
                </span>
              )}
              {((facilitySearchTarget === "address" &&
                existingFacilityMatches.length > 0) ||
                addressSuggestions.length > 0) && (
                <div
                  id="facility-address-suggestions"
                  className="google-address-suggestions"
                  role="listbox"
                  aria-label="Facility suggestions"
                >
                  {facilitySearchTarget === "address" &&
                    existingFacilityMatches.length > 0 && (
                      <ExistingFacilitySuggestions
                        id="facility-address-matches"
                        matches={existingFacilityMatches}
                        onSelect={selectExistingFacility}
                      />
                    )}
                  {addressSuggestions.length > 0 && (
                    <>
                      {facilitySearchTarget === "address" &&
                        existingFacilityMatches.length > 0 && (
                        <div className="google-address-group-label">
                          Google Maps
                        </div>
                      )}
                  {addressSuggestions.map((suggestion) => {
                    const prediction = suggestion.placePrediction;
                    return (
                      prediction && (
                        <button
                          type="button"
                          role="option"
                          className="google-address-suggestion"
                          key={prediction.placeId}
                          onClick={() => void selectAddress(prediction)}
                        >
                          <strong>
                            {prediction.mainText?.toString() ||
                              prediction.text.toString()}
                          </strong>
                          {prediction.secondaryText && (
                            <span>{prediction.secondaryText.toString()}</span>
                          )}
                        </button>
                      )
                    );
                  })}
                    </>
                  )}
                </div>
              )}
            </div>
          </label>
          <label className="full-field">
            Suite or unit
            <input
              value={form.address_line2}
              onChange={(event) => update("address_line2", event.target.value)}
            />
          </label>
          <label>
            City
            <input required readOnly value={form.city} />
          </label>
          <label>
            State / region
            <input required readOnly value={form.region} />
          </label>
          <label>
            ZIP / postal code
            <input required readOnly value={form.postal_code} />
          </label>
          {isAdmin && !initialFacility && (
            <label>
              Account owner email
              <input
                type="email"
                value={form.owner_email}
                onChange={(event) => update("owner_email", event.target.value)}
                placeholder="Optional"
              />
            </label>
          )}
        </div>
        </fieldset>
        {!isAdmin && (
          <fieldset className="pet-facility-selector full-field">
            <legend>Which pets used this facility?</legend>
            {pets.length === 0 ? (
              <span>Add a pet profile before adding a facility.</span>
            ) : (
              pets.map((pet) => (
                <label key={pet.id}>
                  <input
                    type="checkbox"
                    checked={form.pet_ids.includes(pet.id)}
                    onChange={() => togglePet(pet.id)}
                  />
                  {pet.name}
                </label>
              ))
            )}
          </fieldset>
        )}
        {existingFacility && (
          <p className="facility-success" role="status">
            {existingFacility.name} is selected from Actapet.
            <button
              type="button"
              className="facility-change-match"
              onClick={clearExistingFacility}
            >
              Search another
            </button>
          </p>
        )}
        {!existingFacility && addressSelected && (
          <p className="facility-success" role="status">
            Address verified by Google Maps
          </p>
        )}
        {addressError && (
          <p className="form-error" role="alert">
            {addressError}
          </p>
        )}
        {!existingFacility && (
          <div className="facility-vet-editor">
          <div className="facility-team-heading">
            <div>
              <h3>Veterinarians</h3>
              <p>
                {isAdmin
                  ? "Add veterinarians associated with this facility."
                  : "Add one or more veterinarians at this facility."}
              </p>
            </div>
            <button
              type="button"
              className="button button-secondary"
              onClick={() =>
                setForm((current) => ({
                  ...current,
                  veterinarians: [
                    ...current.veterinarians,
                    { name: "", email: "", phone: "", license_number: "" },
                  ],
                }))
              }
            >
              <Plus size={15} /> Add veterinarian
            </button>
          </div>
          {form.veterinarians.map((vet, index) => (
            <div className="facility-vet-form" key={index}>
              <label>
                Name
                <input
                  value={vet.name}
                  onChange={(event) =>
                    updateVet(index, "name", event.target.value)
                  }
                />
              </label>
              <label>
                License number
                <input
                  value={vet.license_number || ""}
                  onChange={(event) =>
                    updateVet(index, "license_number", event.target.value)
                  }
                />
              </label>
              <label>
                Email
                <input
                  type="email"
                  value={vet.email || ""}
                  onChange={(event) =>
                    updateVet(index, "email", event.target.value)
                  }
                />
              </label>
              <button
                type="button"
                className="icon-button danger-icon"
                onClick={() =>
                  setForm((current) => ({
                    ...current,
                    veterinarians: current.veterinarians.filter(
                      (_, vetIndex) => vetIndex !== index,
                    ),
                  }))
                }
                aria-label="Remove veterinarian"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
          </div>
        )}
        {error && (
          <p className="form-error" role="alert">
            {error}
          </p>
        )}
        <div className="modal-actions">
          <button
            type="button"
            className="button button-secondary"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="button button-primary"
            disabled={saving || (!isAdmin && pets.length === 0)}
          >
            {saving
              ? "Adding..."
              : initialFacility
                ? "Save changes"
                : existingFacility
                  ? "Add to My Facilities"
                  : "Create facility"}
          </button>
        </div>
      </form>
    </div>
  );
}
